import { PropsWithChildren, useState } from 'react'
import { WIcon, WParagraph } from '@alice-health/wonderland-react'
import classnames from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'

export interface AccordionProps {
  title: string
  label?: string
  titleVariant?: 'plain' | 'heavy'
  startOpen?: boolean
  border?: boolean
  bleeded?: boolean
}

export const Accordion = ({
  title,
  label,
  titleVariant = 'plain',
  startOpen = false,
  border = true,
  bleeded = false,
  children
}: PropsWithChildren<AccordionProps>) => {
  const [isOpen, setIsOpen] = useState(startOpen)

  const handleAccordionClicked = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div
      className={
        border
          ? 'border-b-gl-border-width-xs border-b-sys-color-stroke-active'
          : ''
      }
    >
      <div
        className={classnames(
          'flex flex-col justify-start py-gl-spacing-02',
          bleeded ? 'px-0' : 'px-gl-spacing-02'
        )}
      >
        {label ? (
          <WParagraph
            size='medium'
            mode='info'
          >
            {label}
          </WParagraph>
        ) : null}

        <div className='cursor-pointer flex justify-between items-center align-middle'>
          <button
            id='arccordion-button'
            data-testid='arccordion-button'
            className={classnames(
              'w-full flex justify-between text-gl-font-size-lg',
              titleVariant === 'heavy' && 'font-gl-font-weight-semibold'
            )}
            type='button'
            aria-label={title}
            aria-expanded={isOpen}
            aria-controls='accordion-content'
            onClick={handleAccordionClicked}
          >
            {title}

            <WIcon
              icon={isOpen ? 'icChevronArrowUp' : 'icChevronArrowDown'}
              size='medium'
            />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            key='accordion-content'
            id='accordion-content'
            role='region'
            aria-labelledby='arccordion-button'
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.1, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            {children}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
